import {
  ScopedSocialSharingData,
  ShareDropdown,
  toCopyLink,
  toSharable,
} from 'components/Dropdown/Dropdown';
import { DropdownAnchor } from 'components/Dropdown/DropdownAnchor';
import { ClaimPoapBody } from 'components/Egg/POAPModal';
import { Questionmark } from 'components/Icons/Icons';
import { ShareDropdownButton } from 'components/Sharing/ShareDropdownButton';
import Spinner from 'components/Spinner';
import { useUser } from 'hooks/useUser';
import React, { useEffect, useState } from 'react';
import { gtag } from 'utils/analytics';
import { getPOAPs, POAP } from 'utils/PointsAPI';
import { nftMarketplaceUrl, SocialMediaType } from 'utils/socials';
import './BadgeCollection.scss';

export const BadgeCollection = () => {
  const [poaps, setPoaps] = useState<POAP[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const unclaimedPoap = poaps.find((poap) => poap && !poap.isClaimed);
  const { account } = useUser();

  const getUserPoaps = async (userId: string) => {
    setIsLoading(true);
    let poaps = await getPOAPs(userId);
    if (poaps.length && poaps[0].isClaimed && !poaps[0].tokenId) {
      // this is necessary because on the first refresh after claiming a poap, the tokenId won't show yet
      // which will mess up sharing
      await new Promise((resolve) =>
        setTimeout(async () => {
          poaps = await getPOAPs(userId);
          resolve(null);
        }, 12000),
      );
    }
    setIsLoading(false);
    setPoaps(poaps);
  };

  useEffect(() => {
    if (account.dusktech?.userId) {
      getUserPoaps(account.dusktech.userId);
    }
  }, [account.dusktech?.userId]);

  return (
    <div className="badge-collection mt-3">
      <div className="badge-collection-title">
        <h3>POAPs</h3>
        <div className="poap-title-tooltip-wrapper">
          <Questionmark />
          <div className="poap-title-tooltip">
            <h4>What is a POAP?</h4>
            <p>
              POAPs are a new way of keeping a reliable record of life experiences. Each time they
              take part on an event, POAP collectors get a unique badge that is supported by a
              cryptographic record.
            </p>
          </div>
        </div>
      </div>
      {poaps.length === 0 && !isLoading && <p className="no-poaps">You don&apos;t own any poaps</p>}
      {unclaimedPoap && (
        <ClaimPoapBody
          poapId={unclaimedPoap.id}
          onClaim={() => window.location.reload()}
          poapImage={unclaimedPoap.imageUrl}
        />
      )}
      {isLoading && <Spinner color="white" />}

      {!unclaimedPoap &&
        !isLoading &&
        poaps.map((poap) => (
          <EggsperiencePoap tokenId={poap.tokenId} imgUrl={poap.imageUrl} key={poap.id} />
        ))}
    </div>
  );
};

type PoapProps = {
  tokenId: string;
  imgUrl: string;
};

const EggsperiencePoap = ({ tokenId, imgUrl }: PoapProps) => {
  return (
    <div className="egg-poap">
      <div className="egg-poap-image">
        <img src={imgUrl} alt="eggsperience-poap" />
        <div className="egg-poap-description">
          <h3>Eggsperience POAP</h3>
          <p>This POAP is for participating in Stages 1-3 of the Gribble Eggsperience.</p>
        </div>
      </div>
      <div className="egg-poap-details">
        <p>Eggsperience POAP</p>
        <DropdownAnchor>
          <EggsperiencePoapSharing tokenId={tokenId} />
        </DropdownAnchor>
      </div>
    </div>
  );
};

const EggsperiencePoapSharing = ({ tokenId }: Omit<PoapProps, 'imgUrl'>) => {
  const poapLink = nftMarketplaceUrl.poap(tokenId);
  const supportedShareTargets: SocialMediaType[] = ['twitter', 'telegram', 'reddit', 'facebook'];

  const fallback = {
    text: `Check out my DuskBreakers “Original Gribble Squad” POAP on POAP.xyz that I received for being an early participant in the Gribble Eggsperience! ${poapLink.toString()}`,
    link: poapLink,
  };

  const data: ScopedSocialSharingData = {
    twitter: {
      text: `Check out my @DuskBreakers “Original Gribble Squad” POAP on @poapxyz that I received for being an early participant in the Gribble Eggsperience! ${poapLink.toString()}`,
      link: poapLink,
    },
    fallback,
  };
  return (
    <ShareDropdownButton data={fallback}>
      <ShareDropdown
        name="Share"
        sections={[[toCopyLink(poapLink.toString())], toSharable(supportedShareTargets, data)]}
        onClick={() => gtag('event', 'poap share')}
      />
    </ShareDropdownButton>
  );
};
